import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as MoreIcon } from '@assets/icons/more.svg';

import LoaderComponent from '@components/LoaderComponent';
import NoData from '@components/NoData';
import { toastError } from '@components/Toast';

import ApiClient from '@lib/apiClient';

import { changeDateFormat, getUserInfo, isPageReachBottom } from '@utils/Util';

const TeamList = ({ search, totalItems }) => {
	const [loading, setLoading] = useState(false);
	const filterData = useSelector((store) => store.filterFieldStore);
	const stateRef = useRef();
	const navigate = useNavigate();

	const getTeamList = (page, filterData, search) => {
		setLoading(true);
		let params = {
			page: page,
			size: 20,
			search: search && search.length > 0 ? search : null,
			departmentIdList: filterData?.departments?.map((item) => item.key) || null,
			teamLeadIdList: filterData?.teamLeads?.map((item) => item.key) || null,
			teamMemberIdList: filterData?.teamMembers?.map((item) => item.key) || null,
			sort: 'id,desc'
		};
		ApiClient.GET('getAllTeams', params)
			.then(({ data }) => {
				totalItems(`${data.totalElements} Teams`);
				let previousList =
					page > 0 && stateRef.current && stateRef.current.allTeams
						? stateRef.current.allTeams
						: [];
				let newList = [...previousList, ...data.data];
				stateRef.current = { ...data, allTeams: newList };
			})
			.catch((response) => {
				let message = response?.error?.message || 'Something went wrong';
				toastError(message);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const disableTeam = (id) => {
		setLoading(true);
		ApiClient.DELETE('teamOperation', {}, id)
			.then(() => {
				let prevState = { ...stateRef.current };
				let allTeams = prevState.allTeams.filter((item) => item.id !== id);
				stateRef.current = { ...prevState, allTeams: allTeams };
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			getTeamList(stateRef.current.currentPage + 1);
		}
	};

	useEffect(() => {
		getTeamList(0, filterData, search);
	}, [filterData, search]);

	useEffect(() => {
		stateRef.current = {
			allTeams: [],
			currentPage: 0
		};

		getTeamList(0);

		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const renderTeamMembers = (row) => {
		return (
			<td className="cursor-pointer">
				<div className="add-buyer">
					<div className="added-members px-0">
						{row?.teamMembers.slice(0, 4).map((member, idx) => {
							return (
								<img
									key={`team_member_${idx}`}
									src={
										member.profilePicDocument?.docUrl ||
										`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
									}
									alt
									data-toggle="tooltip"
									data-placement="top"
									title
									data-original-title={member.name}
								/>
							);
						})}
						{row?.teamMembers?.length > 4 && (
							<div className="more-people">
								<span>+{row?.teamMembers?.length - 4}</span>
							</div>
						)}
					</div>
					<div className="add-member-popup cursor-pointer teamlist-member-popup">
						<div className="assign-member shadow open">
							<div className="semibold-14 title">Team Member</div>
							<div className="member-list-container scroll-y-label">
								{row?.teamMembers.map((member) => {
									return (
										<div className="member-list" key={`member_${member.id}`}>
											<img
												src={
													member.profilePicDocument?.docUrl ||
													`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
												}
												alt=""
											/>
											<div className="name">
												{member.name}
												{getUserInfo()?.id === member.id && (
													<span className="">(You)</span>
												)}
												{member.designation && (
													<span className="tag ml-1">{member.designation}</span>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</td>
		);
	};

	return (
		<LoaderComponent loading={loading}>
			<div className="admin-list-table team-member-list-table">
				<table className="table with-border">
					<thead>
						<tr>
							<th className="type-name">Team name</th>
							<th className="team-lead">Team lead</th>
							<th className="department">Department</th>
							<th className="description">Team member</th>
							<th className="description">Created by</th>
							<th>Action</th>
						</tr>
					</thead>
					<tbody>
						{stateRef.current?.allTeams.map((row, idx) => {
							return (
								<tr key={`team_${idx}`}>
									<td>{row.name}</td>
									<td>{row.teamLead?.name}</td>
									<td>{row.departments.map((item) => item.name).toString()}</td>
									{renderTeamMembers(row)}
									<td>
										<p className="gray_dark_02">{row.createdBy?.name}</p>
										<p className="gray_dark_03">
											{changeDateFormat(row.createdAT, 'YYYY-MM-DD', 'DD MMM, YYYY')}
										</p>
									</td>
									<td>
										<div className="option">
											<div className="dropdown">
												<button
													className="btn btn-default dropdown-toggle"
													type="button"
													id="menu1"
													data-toggle="dropdown"
													aria-expanded="true"
												>
													<MoreIcon />
												</button>
												<ul
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													aria-labelledby="menu1"
													x-placement="bottom-end"
												>
													<li
														role="presentation"
														className="px-4 pb-3 pt-3"
														onClick={() => navigate(`/teams/edit/${row.id}`)}
													>
														<span className="font-weight-normal  text-black cursor-pointer">
															Edit
														</span>
													</li>
													<li
														role="presentation"
														className="px-4 pb-3"
														onClick={() => disableTeam(row.id)}
													>
														<span className="font-weight-normal  text-black cursor-pointer">
															Disable
														</span>
													</li>
												</ul>
											</div>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				{stateRef.current?.allTeams.length === 0 && <NoData message={'No result found'} />}
			</div>
		</LoaderComponent>
	);
};

export default TeamList;
